import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Badge } from '../Badge';
import { Modal } from '../Modal/Modal';
import { NotificationItem } from './notificationConstants';

export interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: NotificationItem[];
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onViewAll?: () => void;
  className?: string;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkAsRead: _onMarkAsRead,
  onMarkAllAsRead,
  onViewAll,
  className,
}) => {
  const { t } = useTranslation();
  const [localNotifications, setLocalNotifications] = useState(notifications);

  const handleMarkAllAsRead = () => {
    setLocalNotifications(prev => prev.map(notification => ({ ...notification, isRead: true })));
    onMarkAllAsRead?.();
  };

  const getNotificationIcon = (type: string) => {
    const icons = {
      call: 'phone',
      warning: 'warning',
      analytics: 'analytics',
      event: 'event',
      system: 'system_update',
    };
    return icons[type as keyof typeof icons] || 'notifications';
  };

  const getNotificationColor = (type: string) => {
    const colors = {
      call: 'text-blue-600 dark:text-blue-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      analytics: 'text-green-600 dark:text-green-400',
      event: 'text-purple-600 dark:text-purple-400',
      system: 'text-gray-600 dark:text-gray-400',
    };
    return colors[type as keyof typeof colors] || 'text-gray-600 dark:text-gray-400';
  };

  const unreadCount = localNotifications.filter(n => !n.isRead).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('notifications.title')}
      size="lg"
      className={className}
    >
      <div className="max-h-96 overflow-y-auto">
        <div className="space-y-3">
          {localNotifications.map(notification => (
            <div
              key={notification.id}
              className={`
                flex items-start space-x-3 p-3 rounded-lg border
                ${
                  notification.isRead
                    ? 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                    : 'bg-white dark:bg-gray-800 border-primary-200 dark:border-primary-700'
                }
              `}
            >
              {/* Notification Icon */}
              <div
                className={`
                flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                ${
                  notification.isRead
                    ? 'bg-gray-100 dark:bg-gray-600'
                    : 'bg-primary-100 dark:bg-primary-900'
                }
              `}
              >
                <span
                  className={`material-icons text-sm ${getNotificationColor(notification.type)}`}
                >
                  {getNotificationIcon(notification.type)}
                </span>
              </div>

              {/* Notification Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4
                      className={`
                      text-sm font-medium
                      ${
                        notification.isRead
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-900 dark:text-white'
                      }
                    `}
                    >
                      {notification.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {notification.time}
                    </p>
                  </div>

                  {/* Unread Badge */}
                  {!notification.isRead && (
                    <div className="ml-2">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                  )}
                </div>

                {/* Notification Actions */}
                {notification.actions && notification.actions.length > 0 && (
                  <div className="flex items-center space-x-2 mt-2">
                    {notification.actions.map((action, index) => (
                      <Button
                        key={index}
                        variant="ghost"
                        size="sm"
                        onClick={action.onClick}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        <span className="material-icons text-sm mr-1">{action.icon}</span>
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {localNotifications.length === 0 && (
            <div className="text-center py-8">
              <span className="material-icons text-4xl text-gray-400 dark:text-gray-500 mb-2">
                notifications_none
              </span>
              <p className="text-gray-500 dark:text-gray-400">
                {t('notifications.noNotifications')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <Badge variant="default" size="sm">
              {unreadCount} {t('notifications.unread')}
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            {t('notifications.markAllAsRead')}
          </Button>
          <Button variant="primary" size="sm" onClick={onViewAll}>
            {t('notifications.viewAllNotifications')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
